// One table of a HOSTED project database: its rows, paged, sorted and filtered, with the data
// dictionary in the column headers (type, personal data, key link, title and description).
// A foreign-key value links to the row it references, and its "Preview" button shows that row's
// columns above the table without leaving the page (the lookup, ReferencedRow.jsx); "Download
// CSV" exports the rows of the current filter and sort. GET /api/projects/{id}/data/tables/{table}[/rows|/export.csv]
// (operate/browse.py). The filter, sort and page live in the URL (browseQuery.js).
// Who may change the table's rows (`can_write`) gets "Add row" and an "Edit" button per row:
// a form generated from the hosted design (RowForm.jsx; routers/data_rows.py), and "Import file":
// a CSV / TSV / XLSX file with a validation preview (ImportPanel.jsx; routers/data_imports.py).
// A `<column>_object_id` cell shows the file it links (name, size, checksum) with a Download
// button (the page's `files`, operate/browse.py; liveFiles.js).
// A column marked personal data says so in its header; masked for the reader (`masked`), its
// cells read *** and it is neither sortable nor offered as a filter (the API answers 403).
// Every row has an "Open" button: the whole row as a card (RowCard, a Sheet: full width on a
// phone), each column with its title and value as the page received it — masked cells stay ***
// — with the key links and the file downloads, and "Edit" there for whoever may edit. On a phone
// the table scrolls inside its box (data-table-scroll), and the card is how a row is read.
// A cell never hides its button: in a file or key cell the text shrinks (ellipsis) and Download /
// Preview keep their size; the file name, size and checksum are the cell's tooltip. Each column
// is resized on its header's right edge (a role=separator handle: drag, ←/→, Home or a double
// click to fit), the widths kept per project and table in this browser (useColumnWidths.js) with
// "Reset column widths"; the Actions column stays in view while the rows scroll sideways. Below
// 768 px the handles are hidden (a drag would fight the scroll) and the row card is the way in.
import { useLayoutEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../../api";
import AppLayout from "../../components/AppLayout";
import { Empty, ErrorNote, Loading } from "../../components/Feedback";
import { Badge, Btn, Card, Field, Input, Select, Sheet, SheetActions, SheetHeader,
         useNarrowScreen } from "../../components/Shared";
import TemplateDownloads from "./TemplateDownloads";
import { useApi } from "../../useApi";
import ImportPanel from "./ImportPanel";
import { resultText } from "./importUtils";
import { downloadPath, fileOf, fileSize } from "./liveFiles";
import { RowDetails, useReferencedRow } from "./ReferencedRow";
import RowForm from "./RowForm";
import useColumnWidths, { clampWidth, widthsKey } from "./useColumnWidths";
import { formatFilter, nextSort, OPS, opLabel, PAGE_SIZES, parseFilter, query, readState,
         referenceLink } from "./browseQuery";

const MONO = "'JetBrains Mono', monospace";
const srOnly = { position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)",
                 whiteSpace: "nowrap" };
const cell = { padding: "8px 14px", borderTop: "1px solid #e5e7eb", fontSize: 13, verticalAlign: "top",
               whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const CELL_PAD = 29;              // a cell's horizontal padding (2 × 14) and a pixel to spare
const AUTO_MAX = 360;             // a cell's width until a column is resized (no maximum after)
const STEP = 16;                  // ←/→ on a resize handle; with Shift: 64
// The Actions column (Open / Edit) stays at the left while the rows scroll sideways.
const actionsCell = { padding: "8px 14px", borderTop: "1px solid #e5e7eb", fontSize: 13, verticalAlign: "top",
                      whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 1, background: "#fff",
                      borderRight: "1px solid #e5e7eb" };
// A file or key cell: one row whose text shrinks with an ellipsis, never its button.
const shrinkRow = { display: "flex", gap: 8, alignItems: "center", minWidth: 0 };
const shrinkText = { minWidth: 0, flex: "0 1 auto", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const cellButton = { border: "1px solid #d1d5db", background: "#fff", borderRadius: 6, cursor: "pointer",
                     flexShrink: 0, whiteSpace: "nowrap" };

function display(v) {
  if (v === null || v === undefined) return null;
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

/** A file cell's tooltip: the name and size, then the checksum. */
export function fileTitle(file) {
  const size = fileSize(file.size);
  return `${file.filename}${size ? ` (${size})` : ""}${file.checksum ? `\n${file.checksum}` : ""}`;
}

// In the table (`wrap` false) the name is cut with an ellipsis; in the row card (`wrap`) it wraps.
// Either way the Download button keeps its size.
function FileCell({ file, wrap = false, maxWidth }) {
  const [error, setError] = useState(null);
  const get = () => api.download(downloadPath(file.download), file.filename).catch(setError);
  const text = wrap ? { minWidth: 0, overflowWrap: "anywhere", whiteSpace: "normal" } : shrinkText;
  return (
    <span data-cell={wrap ? undefined : ""}
          style={{ ...shrinkRow, maxWidth, flexWrap: wrap ? "wrap" : "nowrap" }}>
      <span data-cell-text="" style={text} title={wrap ? fileTitle(file) : undefined}>
        <span style={{ fontFamily: MONO }}>{file.filename}</span>{" "}
        <span style={{ color: "#4b5563" }}>({fileSize(file.size)})</span>
      </span>
      <button type="button" onClick={get} aria-label={`Download ${file.filename}`}
              style={{ ...cellButton, padding: "1px 8px", fontSize: 12 }}>
        Download
      </button>
      {error && <span role="alert" style={{ color: "#b91c1c", whiteSpace: "normal" }}>{error.message}</span>}
    </span>
  );
}

// The row a foreign-key cell names, shown above the table (the cell's Preview button toggles it).
function ReferencePreview({ projectId, preview, onClose }) {
  const { fk, value } = preview;
  const ref = useReferencedRow(projectId, fk, value, { delayMs: 0 });
  const name = `${fk.table} row ${value}`;
  return (
    <section id="fk-preview" aria-label={name}
             style={{ marginBottom: 14, padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 8,
                      background: "#f9fafb" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", alignItems: "center",
                    marginBottom: 6 }}>
        <h2 style={{ margin: 0, fontSize: 14 }}>
          <span style={{ fontFamily: MONO }}>{fk.table}</span> where {fk.column} = {String(value)}
        </h2>
        <span style={{ display: "inline-flex", gap: 8, alignItems: "center" }}>
          <Link to={referenceLink(projectId, fk, value)} style={{ fontSize: 13, color: "var(--dp-accent-dark)" }}>
            Open in {fk.table}
          </Link>
          <Btn variant="secondary" onClick={onClose}>
            <span aria-label="Close the preview">Close</span>
          </Btn>
        </span>
      </div>
      {ref.loading && <Loading label="Loading the row…" />}
      <ErrorNote error={ref.error} />
      {ref.row && <RowDetails columns={ref.columns} row={ref.row} />}
      {!ref.loading && !ref.error && !ref.row && ref.key !== null && (
        <p style={{ margin: 0, fontSize: 13, color: "#b45309" }}>No row of {fk.table} has {fk.column} {String(value)}.</p>
      )}
    </section>
  );
}

// One row, every column, as a card. The values are the ones the rows request returned, so a
// masked column reads *** here as in the table (no second request, no second log entry).
export function RowCard({ projectId, table, columns, row, files, primaryKey, canEdit, onEdit, onClose }) {
  const key = primaryKey ? row[primaryKey] : null;
  const name = key != null ? `${table} row ${key}` : `${table} row`;
  return (
    <Sheet labelledBy="row-card-title" onClose={onClose} maxWidth={640}>
      <SheetHeader id="row-card-title" onClose={onClose} closeLabel={`Close ${name}`}
                   title={<><span style={{ fontFamily: MONO }}>{table}</span>{key != null && <> · {String(key)}</>}</>} />
      <dl data-row-card style={{ margin: 0, display: "grid", gap: 10 }}>
        {columns.map(c => {
          const v = display(row[c.name]);
          const file = fileOf(c.name, row[c.name], files);
          let value = v;
          if (v === null) value = <span style={{ color: "#9ca3af" }}>empty</span>;
          else if (file) value = <FileCell file={file} wrap />;
          else if (c.foreign_key) value = (
            <Link data-tap to={referenceLink(projectId, c.foreign_key, v)} onClick={onClose}
                  style={{ color: "var(--dp-accent-dark)" }}>{v}</Link>
          );
          return (
            <div key={c.name} style={{ borderTop: "1px solid #f3f4f6", paddingTop: 8, minWidth: 0 }}>
              <dt style={{ fontSize: 12, color: "#4b5563" }}>
                <span style={{ fontFamily: MONO, fontWeight: 700, color: "#1a1a1a" }}>{c.name}</span>
                {c.name === primaryKey && <span title="primary key"> 🔑</span>}
                {c.title && <> · {c.title}</>}
                {c.sensitivity && (
                  <span style={{ color: "#991b1b" }}> · {c.sensitivity} data{c.masked ? " (masked)" : ""}</span>
                )}
              </dt>
              <dd style={{ margin: "2px 0 0", fontSize: 14, overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>{value}</dd>
            </div>
          );
        })}
      </dl>
      <SheetActions>
        <Btn variant="secondary" onClick={onClose}>Close</Btn>
        {canEdit && key != null && <Btn onClick={() => onEdit(key)}>Edit<span style={srOnly}> {name}</span></Btn>}
      </SheetActions>
    </Sheet>
  );
}

// The handle on a header's right edge: a focusable separator (WAI-ARIA window splitter). A drag
// (pointer captured) or ←/→ (Shift: ×4) changes the width; Home or a double click fits the
// column to its widest rendered cell.
function ResizeHandle({ name, width, min, onStart, onResize, onFit }) {
  const drag = useRef(null);
  const down = e => {
    if (e.button !== undefined && e.button !== 0) return;
    e.preventDefault();
    drag.current = { id: e.pointerId, x: e.clientX, w: onStart() };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom, or the pointer is gone */ }
  };
  const move = e => {
    const d = drag.current;
    if (!d || (d.id !== undefined && e.pointerId !== undefined && e.pointerId !== d.id)) return;
    onResize(d.w + (e.clientX - d.x));
  };
  const up = () => { drag.current = null; };
  const key = e => {
    const step = e.shiftKey ? STEP * 4 : STEP;
    if (e.key === "ArrowRight") onResize(onStart() + step);
    else if (e.key === "ArrowLeft") onResize(onStart() - step);
    else if (e.key === "Home") onFit();
    else return;
    e.preventDefault();
  };
  return (
    <div role="separator" tabIndex={0} aria-orientation="vertical" aria-label={`Resize column ${name}`}
         aria-valuenow={width} aria-valuemin={min}
         title="Drag to resize; double-click to fit"
         onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
         onLostPointerCapture={up} onDoubleClick={onFit} onKeyDown={key}
         onClick={e => e.stopPropagation()}
         style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 10, cursor: "col-resize",
                  touchAction: "none", display: "flex", justifyContent: "flex-end" }}>
      <span aria-hidden="true" style={{ width: 2, margin: "8px 0", background: "#d1d5db", borderRadius: 1 }} />
    </div>
  );
}

function ColumnHeader({ col, sort, onSort, isKey, resize }) {
  const active = sort === col.name ? "ascending" : sort === `-${col.name}` ? "descending" : "none";
  return (
    <th scope="col" aria-sort={active} data-col={col.name}
        style={{ position: "sticky", top: 0, zIndex: 1, background: "#f9fafb", padding: "10px 14px",
                 textAlign: "left", verticalAlign: "top", borderBottom: "1px solid #e5e7eb",
                 minWidth: resize && resize.fixed ? undefined : 120, overflow: "hidden" }}>
      {col.masked ? (
        // personal data, masked for this reader: it cannot be sorted or filtered by (403)
        <span data-fit="" title={col.name}
              style={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "#1a1a1a", display: "block",
                       overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{col.name}</span>
      ) : (
      <button type="button" data-fit="" onClick={() => onSort(col.name)}
              aria-label={`Sort by ${col.name}${active === "ascending" ? " (now ascending)" : active === "descending" ? " (now descending)" : ""}`}
              title={col.name}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", font: "inherit",
                       fontFamily: MONO, fontSize: 13, fontWeight: 700, color: "#1a1a1a",
                       maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {col.name}{isKey && <span title="primary key"> 🔑</span>}
        <span aria-hidden="true" style={{ marginLeft: 6, color: active === "none" ? "#9ca3af" : "var(--dp-accent-dark)" }}>
          {active === "ascending" ? "▲" : active === "descending" ? "▼" : "↕"}
        </span>
      </button>
      )}
      <div style={{ fontSize: 11, fontWeight: 400, color: "#4b5563", marginTop: 3, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <span>{col.type}</span>
        {col.sensitivity && (
          <span style={{ color: "#991b1b" }} title={col.masked ? "Masked for you; a data admin can grant you personal data" : "Your reads of this column are logged"}>
            {col.sensitivity} data{col.masked ? " (masked)" : ""}
          </span>
        )}
        {col.foreign_key && <span>→ {col.foreign_key.table}.{col.foreign_key.column}</span>}
      </div>
      {(col.title || col.description) && (
        <div style={{ fontSize: 11, fontWeight: 400, color: "#6b7280", marginTop: 2, whiteSpace: "normal", maxWidth: 260 }}>
          {[col.title, col.description].filter(Boolean).join(" — ")}
        </div>
      )}
      {resize && resize.handle && (
        <ResizeHandle name={col.name} width={resize.width} min={resize.min}
                      onStart={resize.start} onResize={resize.set} onFit={resize.fit} />
      )}
    </th>
  );
}

function FilterBar({ columns, filters, onChange }) {
  const [form, setForm] = useState({ column: "", op: "eq", value: "" });
  const needsValue = form.op !== "is_null";
  const add = e => {
    e.preventDefault();
    if (!form.column || (needsValue && form.value === "")) return;
    onChange([...filters, formatFilter({ column: form.column, op: form.op, value: needsValue ? form.value : null })]);
    setForm(f => ({ ...f, value: "" }));
  };
  return (
    <section aria-label="Filter rows" style={{ marginBottom: 14 }}>
      <form onSubmit={add} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ minWidth: 200 }}>
          <Field label="Column">
            <Select options={columns.filter(c => !c.masked).map(c => c.name)} value={form.column}
                    onChange={e => setForm(f => ({ ...f, column: e.target.value }))} />
          </Field>
        </div>
        <div style={{ minWidth: 140 }}>
          <Field label="Condition">
            <Select options={OPS} value={form.op} onChange={e => setForm(f => ({ ...f, op: e.target.value || "eq" }))} />
          </Field>
        </div>
        {needsValue && (
          <div style={{ minWidth: 200 }}>
            <Field label="Value">
              <Input value={form.value} onChange={e => setForm(f => ({ ...f, value: e.target.value }))} />
            </Field>
          </div>
        )}
        <div style={{ paddingBottom: 2 }}>
          <Btn type="submit" disabled={!form.column || (needsValue && form.value === "")}>Add filter</Btn>
        </div>
      </form>
      {filters.length > 0 && (
        <ul aria-label="Active filters" style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "flex", gap: 8, flexWrap: "wrap" }}>
          {filters.map((f, i) => {
            const p = parseFilter(f);
            const text = `${p.column} ${opLabel(p.op)}${p.value != null && p.op !== "is_null" ? ` ${p.value}` : ""}`;
            return (
              <li key={`${f}-${i}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px",
                                             borderRadius: 999, background: "var(--dp-accent-bg)", fontSize: 13 }}>
                <span style={{ fontFamily: MONO }}>{text}</span>
                <button type="button" aria-label={`Remove filter ${text}`}
                        onClick={() => onChange(filters.filter((_, j) => j !== i))}
                        style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: 0, color: "#374151" }}>
                  ×
                </button>
              </li>
            );
          })}
          <li>
            <Btn variant="secondary" onClick={() => onChange([])}>Clear filters</Btn>
          </li>
        </ul>
      )}
    </section>
  );
}

// The width a column needs for its widest rendered cell (and its name): a file or key cell is its
// text's full width plus its button; a plain cell its content (scrollWidth: what overflow hides).
function fitWidth(table, column) {
  if (!table) return 0;
  let w = 0;
  const th = table.querySelector(`thead th[data-col="${CSS.escape(column)}"]`);
  const name = th && th.querySelector("[data-fit]");
  if (name) w = Math.max(w, name.scrollWidth + CELL_PAD + 10);       // + the handle
  table.querySelectorAll(`tbody td[data-col="${CSS.escape(column)}"]`).forEach(td => {
    const row = td.querySelector("[data-cell]");
    if (row) {
      const kids = [...row.children];
      const sum = kids.reduce((n, k) => n + (k.matches("[data-cell-text]") ? k.scrollWidth : k.offsetWidth), 0);
      w = Math.max(w, sum + 8 * Math.max(0, kids.length - 1) + CELL_PAD);
    } else {
      w = Math.max(w, td.scrollWidth + 2);
    }
  });
  return w;
}

// The narrowest a column may be: its cells' buttons whole and a few characters of their text
// (80 px when it has no buttons). Narrower, a Download or Preview button would be cut again.
const MIN_TEXT = 32;
function minWidth(table, column) {
  let w = 0;
  if (!table) return w;
  table.querySelectorAll(`tbody td[data-col="${CSS.escape(column)}"] [data-cell]`).forEach(row => {
    const kids = [...row.children].filter(k => !k.matches("[data-cell-text], [role=alert]"));
    const sum = kids.reduce((n, k) => n + k.offsetWidth, 0);
    w = Math.max(w, sum + 8 * kids.length + MIN_TEXT + CELL_PAD);
  });
  return Math.ceil(w);
}

// The widths the browser gave the columns (their header cells).
function measureAuto(table) {
  const next = {};
  if (table) {
    table.querySelectorAll("thead th[data-col]").forEach(th => {
      next[th.dataset.col] = Math.ceil(th.getBoundingClientRect().width);
    });
  }
  return next;
}

export default function DataTablePage() {
  const { projectId, table } = useParams();
  const [params, setParams] = useSearchParams();
  const state = readState(params);
  const base = `/projects/${projectId}/data/tables/${encodeURIComponent(table)}`;
  const project = useApi(`/projects/${projectId}`);
  const meta = useApi(base);
  const qs = query(state);
  const rows = useApi(`${base}/rows${qs ? `?${qs}` : ""}`);
  const [downloadError, setDownloadError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [editor, setEditor] = useState(null);         // null | {key: null (new) | the row's key}
  const [notice, setNotice] = useState("");
  const resumeId = params.get("import");          // a returned import to check and submit again
  const [importing, setImporting] = useState(!!resumeId);
  const [preview, setPreview] = useState(null);       // null | {fk, value, id}: the previewed referenced row
  const [opened, setOpened] = useState(null);         // null | the row shown as a card (RowCard)
  const previewToggle = useRef(null);
  const tableRef = useRef(null);
  const narrow = useNarrowScreen();

  const set = changes => setParams(new URLSearchParams(query({ ...state, ...changes })));
  const name = project.data ? project.data.name : "…";
  const t = meta.data;
  const r = rows.data;
  const colNames = t ? t.columns.map(c => c.name) : null;
  const { widths, fixed, setAll, setWidth, reset } = useColumnWidths(widthsKey(projectId, table), colNames);
  // Measured after each commit (jsdom: all 0): `auto`, the widths the browser gave the columns in
  // the automatic layout — the handles' values, and the first resize fixes every column at the width
  // it had; `floor`, the narrowest each column may be with its cells' buttons whole; `actions`, the
  // width of the row buttons (Open / Edit), which the fixed layout must leave them.
  const [measured, setMeasured] = useState({ auto: {}, floor: {}, actions: 0 });
  useLayoutEffect(() => {
    const el = tableRef.current;
    if (!el) return;
    const floor = {};
    el.querySelectorAll("thead th[data-col]").forEach(th => { floor[th.dataset.col] = minWidth(el, th.dataset.col); });
    let actions = 0;
    el.querySelectorAll("tbody [data-row-actions]").forEach(a => { actions = Math.max(actions, a.offsetWidth); });
    setMeasured(m => {
      const next = { auto: fixed ? m.auto : measureAuto(el), floor, actions: actions ? Math.ceil(actions) + CELL_PAD : 0 };
      return JSON.stringify(next) === JSON.stringify(m) ? m : next;
    });
  }, [t, r, fixed, widths, narrow, preview, editor]);
  const { auto, floor } = measured;
  const actionsWidth = Math.max(measured.actions, 80);
  // a stored width below what the cell's buttons need (stored before, or the buttons grew) is shown at that floor
  const widthOf = c => Math.max(widths[c] ?? clampWidth(auto[c] || 0), floor[c] || 0);
  const startResize = c => {
    if (fixed) return widthOf(c);
    const now = measureAuto(tableRef.current);
    setMeasured(m => ({ ...m, auto: now }));
    const all = {};
    (colNames || []).forEach(n => { all[n] = widths[n] ?? clampWidth(now[n] || 0); });
    setAll(all);
    return all[c];
  };
  const resizeFor = c => ({
    width: widthOf(c), fixed, handle: !narrow,
    start: () => startResize(c),
    min: Math.max(80, floor[c] || 0),
    set: w => { if (!fixed) startResize(c); setWidth(c, Math.max(w, minWidth(tableRef.current, c))); },
    fit: () => {
      if (!fixed) startResize(c);
      setWidth(c, Math.max(fitWidth(tableRef.current, c), minWidth(tableRef.current, c)));
    },
  });
  const tableWidth = fixed && colNames ? colNames.reduce((n, c) => n + widthOf(c), actionsWidth) : undefined;

  const download = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      const q = query(state, { paging: false });
      await api.download(`${base}/export.csv${q ? `?${q}` : ""}`, `${table}.csv`);
    } catch (err) {
      setDownloadError(err);
    } finally {
      setDownloading(false);
    }
  };

  const canEdit = !!(t && t.can_write);
  const editable = canEdit && !!t.primary_key;
  const done = (saved, what) => {
    setEditor(null);
    setNotice(what === "deleted" ? "Row deleted." : `Row ${saved && saved.key != null ? saved.key : ""} ${what}.`);
    rows.reload();
    meta.reload();
  };

  const first = r && r.total ? r.offset + 1 : 0;
  const last = r ? r.offset + r.rows.length : 0;

  return (
    <AppLayout title={table}
               crumbs={[{ label: "Projects", to: "/projects" }, { label: name, to: `/projects/${projectId}` },
                        { label: "Data", to: `/projects/${projectId}/data` }, { label: table }]}>
      <ErrorNote error={meta.error} />
      {meta.loading && !t && <Loading />}
      {t && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              {t.comment && <p style={{ margin: "0 0 6px", fontSize: 14, color: "#374151" }}>{t.comment}</p>}
              <p style={{ margin: 0, fontSize: 13, color: "#4b5563" }}>
                {t.kind === "hub" && <><Badge color="blue">shared entity</Badge>{" "}</>}
                {t.row_count == null ? "" : `${t.row_count.toLocaleString("en")} rows`}
                {t.primary_key && <> · primary key <code>{t.primary_key}</code></>}
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {canEdit && (
                <Btn onClick={() => { setNotice(""); setEditor({ key: null }); }} disabled={!!editor}>Add row</Btn>
              )}
              {canEdit && (
                <Btn variant="secondary" onClick={() => { setNotice(""); setImporting(true); }} disabled={importing}>
                  Import file
                </Btn>
              )}
              <Btn variant="secondary" onClick={download} disabled={downloading}>
                {downloading ? "Preparing CSV…" : state.filters.length ? "Download CSV (filtered)" : "Download CSV"}
              </Btn>
              {fixed && <Btn variant="secondary" onClick={reset}>Reset column widths</Btn>}
            </div>
          </div>
          <div style={{ margin: "-4px 0 14px" }}>
            <TemplateDownloads projectId={projectId} table={table}
                               stem={project.data ? `${project.data.slug}_${table}` : table} />
          </div>
          {notice && <p role="status" style={{ margin: "0 0 12px", fontSize: 13, color: "#006b2d" }}>{notice}</p>}
          {importing && canEdit && (
            <ImportPanel projectId={projectId} table={table} columns={t.columns}
                         onDone={done => { setNotice(done.status === "submitted" ? "Submitted for review: a data steward approves or rejects it on the Data page." : resultText(done.result)); rows.reload(); meta.reload(); }}
                         resumeId={resumeId}
                         onCancel={() => { setImporting(false); if (resumeId) set({}); }} />
          )}
          {editor && (
            <RowForm key={editor.key === null ? "new" : `edit-${editor.key}`} projectId={projectId} table={table}
                     rowKey={editor.key} onDone={done} onCancel={() => setEditor(null)} />
          )}
          <ErrorNote error={downloadError} />
          {preview && (
            <ReferencePreview key={preview.id} projectId={projectId} preview={preview}
                              onClose={() => {
                                const back = previewToggle.current;
                                setPreview(null);
                                if (back) setTimeout(() => back.focus(), 0);
                              }} />
          )}
          {opened && r && (
            <RowCard projectId={projectId} table={table} columns={t.columns} row={opened} files={r.files}
                     primaryKey={t.primary_key} canEdit={editable && !editor}
                     onEdit={key => { setOpened(null); setNotice(""); setEditor({ key }); }}
                     onClose={() => setOpened(null)} />
          )}
          <FilterBar columns={t.columns} filters={state.filters}
                     onChange={filters => set({ filters, offset: 0 })} />
          <ErrorNote error={rows.error} />
          <Card style={{ padding: 0, overflow: "hidden" }}>
            <div data-table-scroll role="region" aria-label={`Rows of ${table}`} tabIndex={0}
                 style={{ overflow: "auto", maxHeight: "70vh" }}>
              <table ref={tableRef}
                     style={{ borderCollapse: "separate", borderSpacing: 0,
                              ...(fixed ? { tableLayout: "fixed", width: tableWidth, minWidth: "100%" } : { width: "100%" }) }}>
                <caption style={srOnly}>Rows of {table}</caption>
                {fixed && (
                  <colgroup>
                    <col style={{ width: actionsWidth }} />
                    {t.columns.map(c => <col key={c.name} style={{ width: widthOf(c.name) }} />)}
                  </colgroup>
                )}
                <thead>
                  <tr>
                    <th scope="col" data-actions=""
                        style={{ position: "sticky", top: 0, left: 0, zIndex: 2, background: "#f9fafb", padding: "10px 14px",
                                 borderBottom: "1px solid #e5e7eb", borderRight: "1px solid #e5e7eb" }}>
                      <span style={srOnly}>Actions</span>
                    </th>
                    {t.columns.map(c => (
                      <ColumnHeader key={c.name} col={c} sort={state.sort} isKey={c.name === t.primary_key}
                                    resize={resizeFor(c.name)}
                                    onSort={col => set({ sort: nextSort(state.sort, col), offset: 0 })} />
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {r && r.rows.map((row, i) => (
                    <tr key={t.primary_key ? String(row[t.primary_key]) : `${r.offset}-${i}`}>
                      <td style={actionsCell}>
                        <span data-row-actions style={{ display: "inline-flex", gap: 6 }}>
                          <Btn variant="ghost" onClick={() => setOpened(row)}>
                            Open<span style={srOnly}> row {t.primary_key ? String(row[t.primary_key]) : r.offset + i + 1}</span>
                          </Btn>
                          {editable && (
                            <Btn variant="secondary" disabled={!!editor || row[t.primary_key] == null}
                                 onClick={() => { setNotice(""); setEditor({ key: row[t.primary_key] }); }}>
                              <span aria-label={`Edit row ${row[t.primary_key]}`}>Edit</span>
                            </Btn>
                          )}
                        </span>
                      </td>
                      {t.columns.map(c => {
                        const v = display(row[c.name]);
                        let content = v;
                        const file = fileOf(c.name, row[c.name], r.files);
                        const inner = fixed ? undefined : AUTO_MAX - 28;
                        if (v === null) content = <span style={{ color: "#9ca3af" }} aria-label="empty">—</span>;
                        else if (file) content = <FileCell file={file} maxWidth={inner} />;
                        else if (c.foreign_key) {
                          const pid = `${c.name}:${v}`;
                          const open = !!preview && preview.id === pid;
                          content = (
                            <span data-cell="" style={{ ...shrinkRow, gap: 6, maxWidth: inner }}>
                              {/* The link is itself the box that shrinks (a block flex item): inside an
                                  inline wrapper it stayed a 15 px inline box, below the phone layer's
                                  44 px target (a11y check of 2026-10-21). */}
                              <Link data-cell-text="" to={referenceLink(projectId, c.foreign_key, v)}
                                    title={`Open ${c.foreign_key.table} where ${c.foreign_key.column} = ${v}`}
                                    style={{ ...shrinkText, display: "block", color: "var(--dp-accent-dark)" }}>
                                {v}
                              </Link>
                              <button type="button" aria-expanded={open} aria-controls={open ? "fk-preview" : undefined}
                                      aria-label={`Preview ${c.foreign_key.table} row ${v}`}
                                      title={`Show the ${c.foreign_key.table} row here`}
                                      onClick={e => {
                                        previewToggle.current = e.currentTarget;
                                        setPreview(open ? null : { fk: c.foreign_key, value: row[c.name], id: pid });
                                      }}
                                      style={{ ...cellButton, padding: "0 6px", fontSize: 11, lineHeight: "18px" }}>
                                {open ? "Hide" : "Preview"}
                              </button>
                            </span>
                          );
                        }
                        return (
                          <td key={c.name} data-col={c.name} title={file ? fileTitle(file) : v ?? undefined}
                              style={fixed ? cell : { ...cell, maxWidth: AUTO_MAX }}>
                            {content}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
          {rows.loading && !r && <Loading label="Loading rows…" />}
          {r && r.total === 0 && <Empty>No rows match.</Empty>}
          {r && (
            <nav aria-label="Pages" style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
              <span role="status" style={{ fontSize: 13, color: "#374151" }}>
                Rows {first}–{last} of {r.total.toLocaleString("en")}
              </span>
              <Btn variant="secondary" disabled={state.offset === 0}
                   onClick={() => set({ offset: Math.max(0, state.offset - state.limit) })}>Previous</Btn>
              <Btn variant="secondary" disabled={last >= r.total}
                   onClick={() => set({ offset: state.offset + state.limit })}>Next</Btn>
              <div style={{ minWidth: 140 }}>
                <Field label="Rows per page">
                  <Select options={PAGE_SIZES.map(n => ({ value: String(n), label: String(n) }))}
                          value={String(state.limit)}
                          onChange={e => e.target.value && set({ limit: parseInt(e.target.value, 10), offset: 0 })} />
                </Field>
              </div>
            </nav>
          )}
        </>
      )}
    </AppLayout>
  );
}
